// @react
import React from 'react'
import { useMemo, useState } from 'react'
// @common
import strings from 'lib/constants/strings'
import config from 'constants/config'
import Helper, { ActivityAction } from 'utils/Helper.class'
import usePagination from 'modules/common/hooks/usePagination'
// @graphql
import { useQuery } from '@apollo/client'
import {
  FilterOrder,
  GetUserNetworkActivityQuery,
  GetUserNetworkActivityQueryVariables,
  IAbstractSearchItem,
  NetworkActivityOrderByList,
  PublicUserConnectionType,
} from 'lib/graphql/graphqlTypes'
import { GET_USER_NETWORK_ACTIVITY } from 'graphql/queries/userNetwork.query'
// @components
import PublicUsersList from 'modules/common/components/PublicUserList'
import Button from '@bit/atd.web.button'
import Spacer from '@bit/atd.web.spacer'
import Icon from '@bit/atd.web.icons'
import icons from '@bit/atd.web.icons/getIcons'
import Grid from '@bit/atd.web.grid'
import ContentCard from 'modules/common/components/ContentCard'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import CardActivity from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Activity'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
import QueryError from 'modules/common/components/Errors/QueryError'
// @libraries
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'

/**
 * 4.0.3.1 4.0.3 My ATD - Activity History - Network Activity  - Content
 * UX: https://www.figma.com/file/jCo0D2EYkVbuBX1TjR1enA/ATD-MyATD-Desktop-2020?node-id=3233%3A6661
 *
 *  This section is equivalent to a news feed, with content generated based on which users currentUser is following/subscribd to
 *  -- if not following anyone, news feed will be empty
 *  -- if following someone, news feed should be populated (not guranteed, you might be following someone with no generated actitivy)
 *  -- if user is not following anyone, display shortlist of recommended users to follow
 *
 *
 *  fields:
 *    title
 *    description
 *    dateModified (do we need date created or modified here?)
 *    action | created, commented on, shared
 *    url (url of original content)
 *    contentType
 *    user {
 *      firstName
 *      lastName
 *      imageUrl
 *    }
 *
 *
 */

const useStyles = makeStyles({
  textTitle: {
    color: '#2E2d31',
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: '26px',
    marginBottom: '8px',
  },
  textDescription: {
    color: '#424140',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
  },
})

const Content = () => {
  /******************************************
   * STATE
   ******************************************/
  const [sortOrder, setSortOrder] = useState<FilterOrder>(FilterOrder.ASC)
  const [publicUsersCount, setPublicUsersCount] = useState<number>(0)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const { Pagination, paginationProps } = usePagination()

  const {
    loading: isActivityLoading,
    error: isActivityError,
    data: activityData,
  } = useQuery<
    GetUserNetworkActivityQuery,
    GetUserNetworkActivityQueryVariables
  >(GET_USER_NETWORK_ACTIVITY, {
    variables: {
      input: {
        page: paginationProps.currentPage,
        pageSize: config.pageSize,
        orderBy: {
          order: sortOrder,
          value: NetworkActivityOrderByList.CREATED_DATE,
        },
      },
    },
  })

  /******************************************
   * FUNCTIONS
   ******************************************/

  const toggleSortHandler = () => {
    const newOrder =
      sortOrder === FilterOrder.ASC ? FilterOrder.DESC : FilterOrder.ASC
    setSortOrder(newOrder)
  }

  const getOrderGlyph = (): keyof typeof icons => {
    return sortOrder === FilterOrder.ASC ? 'caret-down' : 'caret-up'
  }

  const isActivityData = activityData?.activity?.meta?.count !== 0

  const recommendedUsers = useMemo(() => {
    const publicUsersPageSize = 4

    return !isActivityData ? (
      <>
        <Typography className={classes.textTitle} component={'h3'}>
          {strings.ACTIVITY_HISTORY.NETWORK.NO_RESULTS_MESSAGE}
        </Typography>

        <PublicUsersList
          title={strings.PROFILE.NETWORK.SUGGESTED_USERS_TITLE}
          subtitle={strings.PROFILE.NETWORK.SUGGESTED_USERS_SUBTITLE}
          setCount={setPublicUsersCount}
          queryInput={{
            pageSize: publicUsersPageSize,
            page: 1,
            filters: {
              type: PublicUserConnectionType.RECOMMENDED,
            },
          }}
        />
        {publicUsersCount > publicUsersPageSize && (
          <>
            <Spacer size={26} />
            <Grid container justify="flex-end">
              <Button
                variant="primary"
                href={`${Helper.getPathFromRouteKey('PROFILE_NETWORK')}?tab=3`}
              >
                <span>View All Suggestions</span>
                <Icon glyph="arrow-right" size={16} />
              </Button>
            </Grid>
          </>
        )}
      </>
    ) : (
      <></>
    )
  }, [activityData, isActivityData, publicUsersCount])

  /******************************************
   * RENDER
   ******************************************/
  if (isActivityError || (!activityData && !isActivityLoading)) {
    return <QueryError />
  }

  const actionLabelFormatter = (
    action: ActivityAction,
    content: IAbstractSearchItem,
  ) => {
    const article = `${Helper.articleChooser(content?.contentTypes?.lvl0)}`
    if (!content?.contentTypes?.lvl0) {
      return <></>
    }
    const contentType = `${content?.contentTypes?.lvl0}`
    if (contentType !== 'People') {
      return (
        <>
          {Helper.actionTextHelper(action)} {article} <b>{contentType}</b>
        </>
      )
    }
  }

  /**
   *
   */
  const networkActivityContent = isActivityData && (
    <ConditionalLoader condition={!isActivityLoading}>
      <Grid container>
        <Grid item>
          <span>Sort by: </span>
          <Button onClick={toggleSortHandler} variant="secondary" size="small">
            <>
              Date: {sortOrder === FilterOrder.ASC ? 'Newest' : 'Oldest'}
              <Spacer size={4} />
              <Icon glyph={getOrderGlyph()} size={16} />
            </>
          </Button>
        </Grid>
      </Grid>

      <Spacer fullWidth size={30} />

      <Grid container spacing={3}>
        {activityData?.activity?.data.map((item) => (
          <Grid item xs={12} key={item.id}>
            <CardActivity
              avatar={{
                src: item?.user?.imageUrl ?? '',
                alt: `${item.user.firstName} ${item.user.lastName}`,
                bgColor: Helper.randomColorChooser(),
                textAs: (
                  <p>
                    {Helper.getAcronymFromSentence(
                      `${item.user.firstName} ${item.user.lastName}`,
                    )}
                  </p>
                ),
              }}
              fullName={`${item.user.firstName} ${item.user.lastName}`}
              profileUrl={item.user.url}
              description={
                <span>
                  {actionLabelFormatter(
                    item.action as ActivityAction,
                    item.content as IAbstractSearchItem,
                  )}
                </span>
              }
              timestamp={Helper.globalDateFormatter(item?.content?.updateDate)}
              reference={
                item.content && (
                  <ContentCard
                    typename={item.content.__typename}
                    item={item.content as IAbstractSearchItem}
                  />
                )
              }
            />
          </Grid>
        ))}
      </Grid>

      <PaginationWrapper>
        <Pagination
          {...paginationProps}
          count={activityData?.activity?.meta?.totalPages}
        />
      </PaginationWrapper>
    </ConditionalLoader>
  )

  return (
    <>
      {recommendedUsers}
      {networkActivityContent}
    </>
  )
}
Content.whyDidYouRender = true
export default Content
